import React, { useRef, useEffect, useMemo, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Sparkles,
  PanelLeftOpen,
  Globe,
  Search,
  Zap,
  MessageSquarePlus,
  Loader2,
} from 'lucide-react';
import { useChatStore } from '../store/chatStore';
import { useThemeStore } from '../store/themeStore';
import ChatMessage from './ChatMessage';
import ChatInput from './ChatInput';
import ExploreButton from './ExploreButton';
import {
  createCircularFaviconDataUrl,
  createCircularBadgedFaviconDataUrl,
} from '../utils/faviconBadge';

const BASE_TITLE = 'VNGPT - Trợ lý AI Thông Minh';

const ChatWindow: React.FC = () => {
  const activeChatId = useChatStore((s) => s.activeChatId);
  const chats = useChatStore((s) => s.chats);
  const sidebarOpen = useChatStore((s) => s.sidebarOpen);
  const toggleSidebar = useChatStore((s) => s.toggleSidebar);
  const createNewChat = useChatStore((s) => s.createNewChat);
  const isLoadingChats = useChatStore((s) => s.isLoadingChats);
  const isGenerating = useChatStore((s) => s.isGenerating);
  const theme = useThemeStore((s) => s.theme);

  const messagesEndRef = useRef<HTMLDivElement>(null);

  const activeChat = useMemo(
    () => chats.find((chat) => chat.id === activeChatId),
    [chats, activeChatId]
  );

  const messages = activeChat?.messages || [];
  const lastMessageContent = messages.length > 0 ? messages[messages.length - 1]?.content : '';
  const messageCount = messages.length;

  // ===== Title theo chat =====
  const baseTitle = activeChat?.title ? `${activeChat.title} - VNGPT` : BASE_TITLE;

  // ===== Favicon handling: KHÔNG cần sửa index.html =====
  const iconElsRef = useRef<HTMLLinkElement[]>([]);
  const originalHrefsRef = useRef<string[]>([]);
  const initedRef = useRef(false);

  // tránh re-draw không cần thiết
  const lastAppliedKeyRef = useRef<string>('');

  const initIconEls = useCallback(() => {
    if (initedRef.current) return;
    initedRef.current = true;

    // Lấy tất cả rel icon (bạn đang có nhiều link rel="icon")
    const icons = Array.from(document.querySelectorAll<HTMLLinkElement>('link[rel~="icon"]'));
    iconElsRef.current = icons;
    originalHrefsRef.current = icons.map((el) => el.href);
  }, []);

  const setAllFavicons = useCallback((href: string) => {
    iconElsRef.current.forEach((el) => {
      el.href = href;
    });
  }, []);

  const restoreFavicons = useCallback(() => {
    iconElsRef.current.forEach((el, idx) => {
      const original = originalHrefsRef.current[idx];
      if (original) el.href = original;
    });
  }, []);

  // Optional: Badging API (Chrome/Edge mới; không phải máy nào cũng có)
  const setBadge = useCallback(async (count: number) => {
    const navAny = navigator as any;
    if (typeof navAny.setAppBadge === 'function') {
      try {
        await navAny.setAppBadge(count);
      } catch {}
    }
  }, []);

  const clearBadge = useCallback(async () => {
    const navAny = navigator as any;
    if (typeof navAny.clearAppBadge === 'function') {
      try {
        await navAny.clearAppBadge();
      } catch {}
    }
  }, []);

  const applyIndicator = useCallback(async () => {
    initIconEls();

    const away = document.hidden || !document.hasFocus();

    // Chọn PNG để vẽ (ổn định hơn .ico)
    const baseEl =
      iconElsRef.current.find(
        (el) => (el.type || '').includes('png') || el.href.toLowerCase().endsWith('.png')
      ) || iconElsRef.current[0];

    const baseHref = baseEl?.href;

    // key để tránh vẽ lại liên tục
    const key = `${baseTitle}__${isGenerating ? 'gen' : 'idle'}__${away ? 'away' : 'here'}__${baseHref || 'noicon'}`;
    if (lastAppliedKeyRef.current === key) return;
    lastAppliedKeyRef.current = key;

    // 1) Luôn làm favicon tròn khi đang ở trang Chat (nếu có icon)
    if (baseHref) {
      const circle = await createCircularFaviconDataUrl(baseHref);
      if (circle) setAllFavicons(circle);
    }

    // 2) Bình thường: đang focus hoặc không generate
    if (!isGenerating || !away) {
      document.title = baseTitle;
      await clearBadge();
      return;
    }

    // 3) Away + generating => vừa chấm vừa (1) + badge favicon tròn
    document.title = `(1) ${baseTitle}`;

    if (baseHref) {
      const badged = await createCircularBadgedFaviconDataUrl(baseHref, 1);
      if (badged) setAllFavicons(badged);
    }

    await setBadge(1);
  }, [initIconEls, baseTitle, isGenerating, setAllFavicons, clearBadge, setBadge]);

  // Apply on changes (chat title / generating)
  useEffect(() => {
    applyIndicator();
  }, [applyIndicator]);

  // Listen focus/blur/visibility
  useEffect(() => {
    const onVis = () => applyIndicator();
    const onFocus = () => applyIndicator();
    const onBlur = () => applyIndicator();

    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', onFocus);
    window.addEventListener('blur', onBlur);

    return () => {
      document.removeEventListener('visibilitychange', onVis);
      window.removeEventListener('focus', onFocus);
      window.removeEventListener('blur', onBlur);

      // Cleanup: trả lại title + favicon gốc khi rời component ChatWindow
      document.title = BASE_TITLE;
      restoreFavicons();
      clearBadge();
    };
  }, [applyIndicator, restoreFavicons, clearBadge]);

  // Auto-scroll
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messageCount, lastMessageContent]);

  return (
    <div
      className={`flex-1 flex flex-col h-full min-w-0 ${
        theme === 'dark' ? 'bg-gpt-dark-main' : 'bg-gpt-light-main'
      }`}
    >
      {/* Top Bar */}
      <div
        className={`flex items-center justify-between h-[60px] px-4 flex-shrink-0 ${
          theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
        }`}
      >
        <div className="flex items-center min-w-0">
          <AnimatePresence>
            {!sidebarOpen && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.2 }}
                className="flex items-center gap-1 mr-2"
              >
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleSidebar}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
                      : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
                  }`}
                >
                  <PanelLeftOpen size={20} />
                </motion.button>

                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => createNewChat()}
                  className={`p-2 rounded-lg transition-colors ${
                    theme === 'dark'
                      ? 'hover:bg-gpt-dark-hover text-gpt-dark-text-secondary'
                      : 'hover:bg-gpt-light-hover text-gpt-light-text-secondary'
                  }`}
                >
                  <MessageSquarePlus size={20} />
                </motion.button>
              </motion.div>
            )}
          </AnimatePresence>

          <h1 className="text-base font-semibold truncate">
            {activeChat ? activeChat.title : 'VNGPT'}
          </h1>
        </div>

        <ExploreButton />
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto">
        {isLoadingChats ? (
          <div className="flex items-center justify-center h-full">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex flex-col items-center gap-3"
            >
              <Loader2 size={28} className="animate-spin text-gpt-green" />
              <p
                className={`text-sm ${
                  theme === 'dark'
                    ? 'text-gpt-dark-text-secondary'
                    : 'text-gpt-light-text-secondary'
                }`}
              >
                Loading your chats...
              </p>
            </motion.div>
          </div>
        ) : messages.length === 0 ? (
          <EmptyState theme={theme} />
        ) : (
          <div className="pb-4">
            {messages.map((message, index) => (
              <ChatMessage key={message.id} message={message} isLast={index === messages.length - 1} />
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </div>

      <div className="flex-shrink-0">
        <ChatInput />
      </div>
    </div>
  );
};

const EmptyState: React.FC<{ theme: string }> = React.memo(({ theme }) => {
  const sendChatMessage = useChatStore((s) => s.sendChatMessage);
  const setCurrentMode = useChatStore((s) => s.setCurrentMode);

  const suggestions = useMemo(
    () => [
      { text: 'Giải thích quantum computing bằng tiếng Việt', mode: 'normal' as const, icon: <Sparkles size={14} /> },
      { text: 'Research: Latest AI trends 2025', mode: 'research' as const, icon: <Search size={14} /> },
      { text: 'Deep analysis: Vietnam tech industry', mode: 'deep' as const, icon: <Globe size={14} /> },
      { text: 'Help me write a Python web scraper', mode: 'normal' as const, icon: <Zap size={14} /> },
    ],
    []
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="flex flex-col items-center justify-center h-full px-4"
    >
      {/* Logo thay vì icon Sparkles */}
      <motion.div
        initial={{ scale: 0.8 }}
        animate={{ scale: 1 }}
        transition={{ delay: 0.1, type: 'spring' }}
        className={`w-20 h-20 rounded-full flex items-center justify-center mb-6 overflow-hidden ${
          theme === 'dark' ? 'bg-gpt-dark-input' : 'bg-gpt-light-input'
        }`}
      >
        <img
          src="/logo.png"
          alt="VNGPT Logo"
          className="w-16 h-16 object-contain"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
            const parent = target.parentElement;
            if (parent) {
              const fallback = document.createElement('div');
              fallback.innerHTML = `<svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="${
                theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'
              }"><path d="m12 3-1.912 5.813a2 2 0 0 1-1.275 1.275L3 12l5.813 1.912a2 2 0 0 1 1.275 1.275L12 21l1.912-5.813a2 2 0 0 1 1.275-1.275L21 12l-5.813-1.912a2 2 0 0 1-1.275-1.275L12 3Z"/><path d="M5 3v4"/><path d="M19 17v4"/><path d="M3 5h4"/><path d="M17 19h4"/></svg>`;
              parent.appendChild(fallback);
            }
          }}
        />
      </motion.div>

      <motion.h2
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2 }}
        className={`text-2xl font-semibold mb-2 ${theme === 'dark' ? 'text-gpt-dark-text' : 'text-gpt-light-text'}`}
      >
        How can VNGPT help you today?
      </motion.h2>

      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.25 }}
        className={`text-sm mb-8 ${theme === 'dark' ? 'text-gpt-dark-text-secondary' : 'text-gpt-light-text-secondary'}`}
      >
        Choose a mode: Normal, Research, or Deep Research
      </motion.p>

      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-w-2xl w-full"
      >
        {suggestions.map((s, i) => {
          const mc: Record<string, string> = {
            normal: 'border-emerald-500/20 hover:border-emerald-500/40',
            research: 'border-blue-500/20 hover:border-blue-500/40',
            deep: 'border-purple-500/20 hover:border-purple-500/40',
          };
          const tc: Record<string, string> = {
            normal: 'text-emerald-400',
            research: 'text-blue-400',
            deep: 'text-purple-400',
          };
          return (
            <motion.button
              key={i}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 + i * 0.05 }}
              whileHover={{ scale: 1.02, y: -1 }}
              whileTap={{ scale: 0.98 }}
              onClick={() => {
                setCurrentMode(s.mode);
                sendChatMessage(s.text);
              }}
              className={`text-left px-4 py-3 rounded-xl border text-sm transition-all ${mc[s.mode]} ${
                theme === 'dark'
                  ? 'bg-gpt-dark-input/50 text-gpt-dark-text'
                  : 'bg-gpt-light-input/50 text-gpt-light-text'
              }`}
            >
              <div className="flex items-center gap-2">
                <span className={tc[s.mode]}>{s.icon}</span>
                <span className={`text-[10px] uppercase font-semibold tracking-wider ${tc[s.mode]}`}>
                  {s.mode}
                </span>
              </div>
              <p className="mt-1">{s.text}</p>
            </motion.button>
          );
        })}
      </motion.div>
    </motion.div>
  );
});

EmptyState.displayName = 'EmptyState';

export default ChatWindow;